import { CdkDragDrop } from "@angular/cdk/drag-drop";
import { NgTemplateOutlet } from "@angular/common";
import { ChangeDetectorRef, Component, Input, OnInit, ViewChild, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { IgxTreeSelectionType } from '@infragistics/igniteui-angular/tree';
import { ToastService } from "@tylertech/core-module";
import { TreeComponent } from "@tylertech/forge";
import { ForgeCardModule, ForgeIconButtonModule, ForgeIconModule, ForgeInlineMessageModule, ForgeLinearProgressModule, ForgeScaffoldModule, ForgeToolbarModule, ForgeTooltipModule, ForgeTreeModule } from "@tylertech/forge-angular";
import { Utilities } from "@tylertech/shared-module";
import { ITreeViewMetadata, PropertyMetadata, } from "@tylertech/tyler-api-types";
import { RepositoryFormData } from "../../../interfaces";
import { MetadataActionService, MetadataEventService, MetadataPropertyService, MetadataService, } from "../../../services";
import { ObjectHelpers } from "../../../utilities";
import { FlexModule, FlexLayoutModule } from "@ngbracket/ngx-layout";

export interface ITreeNode
{
    Children: ITreeNode[];
    Disabled: boolean;
    Display: string;
    Icon: string;
    Id: string;
    Selected: boolean | undefined;
    Entity: any;
    Expanded?: boolean;
    HasChildren?: boolean; // Indicates if node has children without loading them
    ChildrenLoaded?: boolean; // Track if children have been loaded
}

export interface DropInfo
{
    targetId: string;
    action?: string;
}

@Component({
    selector: "tree-view",
    templateUrl: "./treeView.tmpl.html",
    styleUrls: ["./treeView.component.scss"],
    imports: [
        ForgeCardModule,
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeInlineMessageModule,
        ForgeLinearProgressModule,
        ForgeScaffoldModule,
        ForgeToolbarModule,
        ForgeTooltipModule,
        ForgeTreeModule,
        NgTemplateOutlet,
        FlexModule,
        FlexLayoutModule
    ]
})
export class TreeViewComponent implements OnInit
{
    private $metadataService = inject(MetadataService);
    private readonly $metadataPropertyService = inject(MetadataPropertyService);
    private $metadataActionService = inject(MetadataActionService);
    private $metadataEventService = inject(MetadataEventService);
    private cdr = inject(ChangeDetectorRef);
    private readonly $toastService = inject(ToastService);

    @ViewChild("forgeTree") forgeTree: TreeComponent;

    @Input()
    viewMetadata: ITreeViewMetadata;

    @Input()
    entities: any[];

    @Input()
    datacontroller: RepositoryFormData;

    @Input() model: any;

    @Input()
    allowDragAndDrop: boolean = true;

    @Input()
    startCollapsed: boolean = true; // Start with all nodes collapsed for better performance

    readonly CASCADING: IgxTreeSelectionType = IgxTreeSelectionType.Cascading;
    readonly SINGLE_NODE: IgxTreeSelectionType = IgxTreeSelectionType.None;
    readonly DEFAULT_SELECT_TYPE = IgxTreeSelectionType.None;

    nodes: ITreeNode[];
    selectType: IgxTreeSelectionType;
    property: PropertyMetadata;

    dropActionTodo: DropInfo | null = null;
    dropTargetIds: string[] = [];
    nodeLookup: { [key: string]: ITreeNode; } = {};
    screenReaderAnnouncement: string = '';
    treeProgress = signal(false);
    allNodesSelected: boolean = false;
    disableCascadingSelection: boolean = false;

    constructor()
    {
        const $metadataEventService = this.$metadataEventService;

        const self = this;
        $metadataEventService.onRecalculateWorking
            .pipe(takeUntilDestroyed())
            .subscribe((args) =>
            {
                if (!args.Recalculating)
                    self.nodes.forEach((node) => self.updateNodeEntities(node));
            });
    }

    ngOnInit(): void
    {
        const self = this;
        this.$metadataService
            .AugmentModels(
                this.viewMetadata.GridProperties.ClassMetadata,
                this.entities,
                this.viewMetadata.SelectProperty
                    ? this.viewMetadata.SelectProperty
                    : "Selected",
            )
            .then(() =>
            {
                self.property =
                    self.viewMetadata.GridProperties.ClassMetadata.Properties[0];

                self.selectType = Utilities.IsNullOrUndefined(self.viewMetadata.SelectType)
                    ? self.DEFAULT_SELECT_TYPE
                    : self.viewMetadata.SelectType as IgxTreeSelectionType;

                self.disableCascadingSelection = self.viewMetadata.DisableCascadingSelection === true;

                self.createTree();

                self.prepareDragDrop(self.nodes);
            });
    }

    get lazyLoadChildren(): boolean
    {
        return this.selectType !== this.CASCADING;
    }

    get treeMode(): 'single' | 'multiple' | 'leaf'
    {
        if (this.selectType !== this.CASCADING)
        {
            return 'single';
        }

        return this.disableCascadingSelection ? 'leaf' : 'multiple';
    }

    createTree(): void
    {
        this.nodes = [];
        this.nodeLookup = {}; // Reset lookup map for better performance
        let isDisabledCallCount = 0;

        this.entities.forEach((entity, index) =>
        {
            if (this.viewMetadata.NodesProperty)
            {
                isDisabledCallCount++;
                const hasChildren = !Utilities.IsNullOrUndefinedOrEmpty(
                    entity[this.viewMetadata.NodesProperty]
                );

                const topNode: ITreeNode = <ITreeNode>{
                    Id: this.getId(entity, index),
                    Display: entity[this.viewMetadata.DisplayProperty],
                    Selected: entity[this.viewMetadata.SelectProperty],
                    Disabled: this.$metadataPropertyService.isDisabled(
                        this.property,
                        entity,
                        entity["$$Parent"],
                    ),
                    Icon: entity[this.viewMetadata.IconProperty],
                    Entity: entity,
                    Expanded: this.startCollapsed ? false : undefined,
                    HasChildren: hasChildren,
                    ChildrenLoaded: false,
                    Children: [],
                };

                this.nodeLookup[topNode.Id] = topNode;

                if (hasChildren)
                {
                    topNode.Children = this.createChildNodes(entity, this.lazyLoadChildren ? 1 : 999);
                    topNode.ChildrenLoaded = !this.lazyLoadChildren; // Only fully loaded if not lazy
                }

                this.nodes.push(topNode);
            }
            //Bottom-up tree
            else if (this.viewMetadata.Nodes)
            {
                // const newNode: ITreeNode = <ITreeNode>{
                //     Id: this.getId(entity, index),
                //     Display: entity[this.viewMetadata.DisplayProperty],
                //     Selected: entity[this.viewMetadata.SelectProperty],
                //     Disabled: this.$metadataPropertyService.isDisabled(this.property, entity, entity["$$Parent"]),
                //     Icon: entity[this.viewMetadata.IconProperty]
                // };
                // let nodesList: string[] = [];
                // this.viewMetadata.Nodes.forEach(n => nodesList.push(entity[n]));
                // if (!nodesList)
                //     return;
                // let currentParent: ITreeNode;
                // nodesList.forEach((n, i) =>
                // {
                //     if (i === 0)
                //         currentParent = getOrCreateNode(n);
                //     else
                //         currentParent = getOrCreateNode(n, currentParent);
                //     if (i === nodesList.length - 1)
                //     {
                //         newNode.Parent = currentParent;
                //         currentParent.Children.push(newNode);
                //     }
                // });
            }
        });

        // function getOrCreateNode(display: string, parentNode?: ITreeNode): ITreeNode
        // {
        //     let children: ITreeNode[];
        //     if (!parentNode)
        //         children = self.nodes;
        //     else
        //         children = parentNode.Children;
        //     if (children.filter(x => x.Display === display).length > 0)
        //         return children.find(x => x.Display === display);
        //     const newNode = <ITreeNode>{
        //         Display: display,
        //         Children: []
        //     };
        //     newNode.Parent = parentNode;
        //     children.push(newNode);
        //     return newNode;
        // }
    }

    get disableDragAndDrop(): boolean
    {
        return this.selectType === this.CASCADING
            ? true
            : !this.viewMetadata.AllowDragDropSort;
    }

    createChildNodes(parentEntity: any, maxDepth: number = 999, currentDepth: number = 1): ITreeNode[]
    {
        return parentEntity[this.viewMetadata.NodesProperty].map(
            (entity, index) =>
            {
                const hasChildren = !Utilities.IsNullOrUndefinedOrEmpty(
                    entity[this.viewMetadata.NodesProperty]
                );

                const treeNode = <ITreeNode>{
                    Id: this.getId(entity, index),
                    Display: entity[this.viewMetadata.DisplayProperty],
                    Selected: entity[this.viewMetadata.SelectProperty],
                    Disabled: this.$metadataPropertyService.isDisabled(
                        this.property,
                        entity,
                        entity["$$Parent"],
                    ),
                    Icon: entity[this.viewMetadata.IconProperty],
                    Entity: entity,
                    HasChildren: hasChildren,
                    ChildrenLoaded: false,
                    Children: [],
                    Expanded: this.startCollapsed ? false : undefined,
                };

                this.nodeLookup[treeNode.Id] = treeNode;

                if (hasChildren && currentDepth < maxDepth)
                {
                    treeNode.Children = this.createChildNodes(entity, maxDepth, currentDepth + 1);
                    treeNode.ChildrenLoaded = true;
                }

                return treeNode;
            },
        );
    }

    expandAll(expand: boolean): void
    {
        this.treeProgress.set(true);

        setTimeout(() =>
        {
            this.expandNodes(this.nodes, expand);

            setTimeout(() =>
            {
                this.treeProgress.set(false);
                this.cdr.detectChanges();
            }, 0);
        }, 0);
    }

    private expandNodes(nodes: ITreeNode[], expand: boolean): void
    {
        if (!nodes) return;

        nodes.forEach(node =>
        {
            if (expand && this.lazyLoadChildren && node.HasChildren && !node.ChildrenLoaded)
            {
                node.Children = this.createChildNodesForNode(node);
                node.ChildrenLoaded = true;
            }

            if (node.HasChildren || (node.Children && node.Children.length > 0))
            {
                node.Expanded = expand;
                if (node.Children && node.Children.length > 0)
                {
                    this.expandNodes(node.Children, expand);
                }
            }
        });
    }

    selectAll(select: boolean): void
    {
        this.entities.forEach(e => this.setSelected(e, select));
        this.selectNodes(this.nodes, select);

        if (this.disableCascadingSelection)
        {
            return;
        }

        if (!select)
        {
            this.forgeTree.value = [];
        }
        else
        {
            const ids: string[] = [];
            this.flattenNodesRecursive(this.nodes);

            this.flattenedNodes.forEach(node =>
            {
                if (!node.Disabled)
                    ids.push(node.Id);
            });

            this.forgeTree.value = ids;
        }
    }

    get independentSelection(): boolean
    {
        return this.selectType === this.CASCADING && this.disableCascadingSelection;
    }

    private toggleIndependentSelection(node: ITreeNode): void
    {
        if (node.Disabled)
        {
            return;
        }

        const select = !node.Selected;

        node.Selected = select;
        if (node.Entity)
        {
            node.Entity[this.viewMetadata.SelectProperty] = select;
        }

        if (!select && node.Children?.length > 0)
        {
            this.selectAllChildNodes(node.Children, false);
        }

        if (select)
        {
            this.selectAllParentNodes(node.Id, true);
        }

        this.announceToScreenReader(`${node.Display} ${select ? 'selected' : 'not selected'}.`);
    }

    private isOwnItemEvent(event: Event): boolean
    {
        return (event.target as HTMLElement).closest('forge-tree-item') === event.currentTarget;
    }

    private isExpandIconEvent(event: Event): boolean
    {
        return event.composedPath().some(el => el instanceof HTMLElement && el.classList.contains('expand-icon'));
    }

    private selectAllNodes(nodes: ITreeNode[], select: boolean): void
    {
        if (!nodes) return;

        nodes
            .filter((n) => !n.Disabled)
            .forEach((node) =>
            {
                node.Selected = select;
                if (node.Children && node.Children.length > 0)
                {
                    this.selectAllNodes(node.Children, select);
                }
            });
    }

    setSelected(
        entity: any,
        select: boolean,
        ids: string[] | null = null,
        index: number | null = null,
        ignoreChildren = false
    ): void
    {
        if ((ids === null || ids.indexOf(this.getId(entity, index)) > -1) &&
            !this.$metadataPropertyService.isDisabled(this.property, entity, entity["$$Parent"],))
        {
            entity[this.viewMetadata.SelectProperty] = select;
        }

        if (!ignoreChildren && entity[this.viewMetadata.NodesProperty])
        {
            entity[this.viewMetadata.NodesProperty].forEach((e: any) =>
                this.setSelected(e, select, ids, index),
            );
        }
    }

    nodeSelection(event: any, selectAll: boolean = false): void
    {
        if (this.keyboardMoveMode || this.selectType === this.SINGLE_NODE) return;

        const nodeId = event.detail;
        const select = this.forgeTree.value.includes(nodeId);

        const node = this.findNodeById(nodeId, this.nodes);
        if (node)
        {
            node.Selected = select;
            if (node.Entity)
            {
                node.Entity[this.viewMetadata.SelectProperty] = select;
            }

            if (node.Children?.length > 0)
            {
                this.selectAllChildNodes(node.Children, select);
            }

            this.updateAncestorSelection(nodeId);

            this.syncForgeTreeValue();
        }
    }

    private findNodeById(id: string, nodes: ITreeNode[]): ITreeNode | null
    {
        return this.nodeLookup[id] || null;
    }

    private updateAncestorSelection(nodeId: string): void
    {
        const ancestors = this.getAncestorNodes(nodeId);
        if (!ancestors || ancestors.length === 0) return;

        for (let i = ancestors.length - 1; i >= 0; i--)
        {
            const ancestor = ancestors[i];

            if (!ancestor.Children || ancestor.Children.length === 0 || ancestor.Disabled)
            {
                continue;
            }

            const nonDisabledChildren = ancestor.Children.filter(child => !child.Disabled);
            const allChildrenSelected = nonDisabledChildren.length > 0 &&
                nonDisabledChildren.every(child => child.Selected === true);

            const previousSelection = ancestor.Selected;
            ancestor.Selected = allChildrenSelected;

            if (ancestor.Entity)
            {
                ancestor.Entity[this.viewMetadata.SelectProperty] = allChildrenSelected;
            }

            if (previousSelection === ancestor.Selected)
            {
                break;
            }
        }
    }

    private syncForgeTreeValue(): void
    {
        const selectedIds: string[] = [];
        this.collectSelectedNodeIds(this.nodes, selectedIds);
        this.forgeTree.value = selectedIds;
    }

    private collectSelectedNodeIds(nodes: ITreeNode[], selectedIds: string[]): void
    {
        if (!nodes) return;

        nodes.forEach(node =>
        {
            if (node.Selected === true && !node.Disabled)
            {
                selectedIds.push(node.Id);
            }
            if (node.Children && node.Children.length > 0)
            {
                this.collectSelectedNodeIds(node.Children, selectedIds);
            }
        });
    }

    private selectTreeNodes(nodes: ITreeNode[]): void
    {
        const selectedIds: string[] = this.forgeTree.value as string[];

        nodes.forEach(node =>
        {
            if (!node.Disabled)
            {
                let notAllChildrenSelected = false;

                if (node.Children?.length > 0)
                {
                    if (node.Children.filter(x => x.Selected === false).length > 0)
                    {
                        notAllChildrenSelected = true;
                    }
                    this.selectTreeNodes(node.Children);
                }
                const selectedId = selectedIds.includes(node.Id);
                node.Selected = notAllChildrenSelected ? undefined : selectedId;
            }
        });
    }

    private selectNodes(
        nodes: ITreeNode[],
        select: boolean,
        ids: string[] | null = null,
    ): void
    {
        nodes
            .filter((n) => !n.Disabled)
            .forEach((x) =>
            {
                if (Utilities.IsNullOrUndefined(ids))
                {
                    x.Selected = select;
                    if (x.Children?.length > 0)
                    {
                        this.selectNodes(x.Children, select, ids);
                    }
                }
                else if (ids.indexOf(x.Id) >= 0)
                {
                    x.Selected = select;

                    if (select && x.Children?.length > 0)
                    {
                        this.selectAllChildNodes(x.Children, select);
                    }
                }
                else if (x.Children?.length > 0)
                {
                    this.selectNodes(x.Children, select, ids);
                }
            });

        if (!Utilities.IsNullOrUndefined(ids))
        {
            this.entities.forEach((e, i) =>
                this.setSelected(e, select, ids, i),
            );
        }
    }

    private selectAllChildNodes(nodes: ITreeNode[], select: boolean): void
    {
        if (!nodes) return;

        nodes
            .filter((n) => !n.Disabled)
            .forEach((node) =>
            {
                node.Selected = select;
                if (node.Entity && this.viewMetadata.SelectProperty)
                {
                    node.Entity[this.viewMetadata.SelectProperty] = select;
                }
                if (node.Children && node.Children.length > 0)
                {
                    this.selectAllChildNodes(node.Children, select);
                }
            });
    }

    private selectAllParentNodes(nodeId: string, select: boolean): void
    {
        this.getAncestorNodes(nodeId)?.forEach(node => {
            node.Selected = select;
            if (node.Entity && this.viewMetadata.SelectProperty)
            {
                node.Entity[this.viewMetadata.SelectProperty] = select;
            }
        });
    }

    get selectTypeClass(): string
    {
        if (Utilities.IsNullOrUndefined(this.selectType))
        {
            return "select-single-node";
        }

        switch (this.selectType)
        {
            case IgxTreeSelectionType.Cascading:
                return "select-cascading";
                break;
            case IgxTreeSelectionType.None:
                return "select-single-node";
                break;
            default:
                return "select-single-node";
        }
    }

    nodeClicked(event: any, node: ITreeNode): void
    {
        this.exitKeyboardMoveMode();

        if (this.independentSelection && this.isOwnItemEvent(event) && !this.isExpandIconEvent(event) && !node.Disabled)
        {
            this.toggleIndependentSelection(node);
            event.stopPropagation();
        }
    }

    treeItemToggle(event: any, node: ITreeNode, expanded: boolean): void
    {
        node.Expanded = expanded;

        if (expanded && this.lazyLoadChildren && node.HasChildren && !node.ChildrenLoaded)
        {
            node.Children = this.createChildNodesForNode(node);
            node.ChildrenLoaded = true;
        }

        event.stopPropagation();
    }

    private createChildNodesForNode(node: ITreeNode): ITreeNode[]
    {
        if (!node.Entity || !this.viewMetadata.NodesProperty)
        {
            return [];
        }

        return this.createChildNodes(node.Entity, 999, 1);
    }

    nodeClick(model: any): void
    {
        const self = this;
        this.$metadataActionService
            .ExecuteAction(
                model,
                this.viewMetadata.NodeAction,
                this.datacontroller,
            )
            .then((_) =>
            {
                self.nodes.forEach((node) => self.updateNodeEntities(node));
            });
    }

    updateNodeEntities(node: ITreeNode): void
    {
        if (node.Entity)
        {
            node.Display = node.Entity[this.viewMetadata.DisplayProperty];
            node.Selected = node.Entity[this.viewMetadata.SelectProperty];
            node.Disabled = this.$metadataPropertyService.isDisabled(
                this.property,
                node.Entity,
                node.Entity["$$Parent"],
            );
            node.Icon = node.Entity[this.viewMetadata.IconProperty];
        }
        if (node.Children)
            node.Children.forEach((child) => this.updateNodeEntities(child));
    }

    getId(entity: any, index: number | null): string
    {
        if (index === null)
        {
            index = -1;
        }
        return ObjectHelpers.GetId(
            this.viewMetadata.GridProperties.IdProperty,
            entity,
            index,
        ).toString();
    }

    get title(): string
    {
        return this.viewMetadata.Title;
    }

    prepareDragDrop(nodes: ITreeNode[]): void
    {
        nodes.forEach(node =>
        {
            this.dropTargetIds.push(node.Id);
            if (node.Children?.length > 0)
            {
                this.prepareDragDrop(node.Children);
            }
        });
    }

    private draggedNode: ITreeNode | null = null;
    private dragOverNode: ITreeNode | null = null;
    private dropPosition: 'before' | 'inside' | 'after' | null = null;
    private keyboardMoveMode: boolean = false;
    private keyboardMovingNode: ITreeNode | null = null;
    private keyboardTargetNode: ITreeNode | null = null;
    private keyboardDropPosition: 'before' | 'inside' | 'after' = 'inside';
    private flattenedNodes: ITreeNode[] = [];

    onDragStart(event: DragEvent, node: ITreeNode): void
    {
        this.draggedNode = node;
        event.dataTransfer!.effectAllowed = 'move';
        event.dataTransfer!.setData('text/html', node.Id);
        (event.target as HTMLElement).style.opacity = '0.4';
        event.stopPropagation();
    }

    onDragEnd(event: DragEvent): void
    {
        (event.target as HTMLElement).style.opacity = '1';
        this.draggedNode = null;
        this.dragOverNode = null;
        this.dropPosition = null;

        this.clearDropIndicators();
    }

    onDragOver(event: DragEvent, node: ITreeNode): void
    {
        if (!this.draggedNode || this.draggedNode.Id === node.Id)
        {
            event.preventDefault();
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        this.dragOverNode = node;

        const forgeTreeItem = event.currentTarget as HTMLElement;
        if (!forgeTreeItem) return;

        const rect = forgeTreeItem.getBoundingClientRect();
        const y = event.clientY - rect.top;
        const height = rect.height;
        const oneThird = height / 3;

        this.clearDropIndicators();

        const isDescendant = this.isDescendantOf(node, this.draggedNode);

        if (isDescendant)
        {
            event.dataTransfer!.dropEffect = 'none';
            this.dropPosition = null;
            return;
        }

        if (y < oneThird)
        {
            this.dropPosition = 'before';
        }
        else if (y > 2 * oneThird)
        {
            this.dropPosition = 'after';
        }
        else
        {
            this.dropPosition = 'inside';
        }

        const draggedNodeLevel = this.getNodeLevel(this.draggedNode);
        let targetLevel: number;
        if (this.dropPosition === 'inside')
        {
            targetLevel = this.getNodeLevel(node) + 1;
        } else
        {
            targetLevel = this.getNodeLevel(node);
        }

        if (draggedNodeLevel !== targetLevel)
        {
            event.dataTransfer!.dropEffect = 'none';
            this.dropPosition = null;
            return;
        }

        event.dataTransfer!.dropEffect = 'move';

        if (this.dropPosition === 'before')
        {
            forgeTreeItem.classList.add('drop-before');
        } else if (this.dropPosition === 'after')
        {
            forgeTreeItem.classList.add('drop-after');
        } else
        {
            forgeTreeItem.classList.add('drop-inside');
        }
    }

    onDragLeave(event: DragEvent): void
    {
        const relatedTarget = event.relatedTarget as HTMLElement;
        const currentTarget = event.currentTarget as HTMLElement;

        if (!relatedTarget || !currentTarget.contains(relatedTarget))
        {
            const forgeTreeItem = currentTarget;
            forgeTreeItem.classList.remove('drop-before', 'drop-inside', 'drop-after');
        }
    }

    validateDropTarget(): boolean
    {
        if (!this.draggedNode || !this.dragOverNode || !this.dropPosition)
        {
            return false;
        }

        const draggedNodeLevel = this.getNodeLevel(this.draggedNode);

        let targetLevel: number;
        if (this.dropPosition === 'inside')
        {
            targetLevel = this.getNodeLevel(this.dragOverNode) + 1;
        } else
        {
            targetLevel = this.getNodeLevel(this.dragOverNode);
        }

        return draggedNodeLevel === targetLevel;
    }

    private getNodeLevel(node: ITreeNode): number
    {
        const ancestors = this.getAncestorNodes(node.Id);
        return ancestors ? ancestors.length : 0;
    }

    onDrop(event: DragEvent, targetNode: ITreeNode): void
    {
        event.preventDefault();
        event.stopPropagation();

        if (!this.draggedNode || this.draggedNode.Id === targetNode.Id || !this.dropPosition || !this.validateDropTarget())
        {
            return;
        }

        if (this.isDescendantOf(targetNode, this.draggedNode))
        {
            this.clearDropIndicators();
            this.draggedNode = null;
            this.dragOverNode = null;
            this.dropPosition = null;
            return;
        }

        const droppedNodeId = this.draggedNode.Id;

        this.removeNodeFromTree(this.draggedNode, this.nodes);

        if (this.dropPosition === 'inside')
        {
            if (!targetNode.Children)
            {
                targetNode.Children = [];
            }
            targetNode.Children.push(this.draggedNode);
            targetNode.Expanded = true;
        } else
        {
            const parentArray = this.findParentArray(targetNode, this.nodes);
            if (parentArray)
            {
                const targetIndex = parentArray.findIndex(n => n.Id === targetNode.Id);
                if (targetIndex > -1)
                {
                    const insertIndex = this.dropPosition === 'before' ? targetIndex : targetIndex + 1;
                    parentArray.splice(insertIndex, 0, this.draggedNode);
                }
            }
        }

        this.clearDropIndicators();
        this.draggedNode = null;
        this.dragOverNode = null;
        this.dropPosition = null;

        setTimeout(() => this.focusNode(droppedNodeId), 0);
    }

    private clearDropIndicators(): void
    {
        document.querySelectorAll('.drop-before, .drop-inside, .drop-after').forEach(el =>
        {
            el.classList.remove('drop-before', 'drop-inside', 'drop-after');
        });
    }

    private removeNodeFromTree(nodeToRemove: ITreeNode, nodes: ITreeNode[]): boolean
    {
        const index = nodes.findIndex(n => n.Id === nodeToRemove.Id);
        if (index > -1)
        {
            nodes.splice(index, 1);
            return true;
        }

        for (const node of nodes)
        {
            if (node.Children && this.removeNodeFromTree(nodeToRemove, node.Children))
            {
                return true;
            }
        }

        return false;
    }

    private findParentArray(targetNode: ITreeNode, nodes: ITreeNode[]): ITreeNode[] | null
    {
        if (nodes.some(n => n.Id === targetNode.Id))
        {
            return nodes;
        }

        for (const node of nodes)
        {
            if (node.Children)
            {
                const result = this.findParentArray(targetNode, node.Children);
                if (result) return result;
            }
        }

        return null;
    }

    dragMoved(event: any): void
    {
        const e = document.elementFromPoint(
            event.pointerPosition.x,
            event.pointerPosition.y,
        );

        if (!e)
        {
            this.clearDragInfo();
            return;
        }
        const container = e.classList.contains("node-item")
            ? e
            : e.closest(".node-item") ?? e.closest("forge-tree-item");
        if (!container)
        {
            this.clearDragInfo();
            return;
        }

        const targetId = container.getAttribute("data-id") ?? container.getAttribute("id");
        if (!targetId)
        {
            this.clearDragInfo();
            return;
        }

        this.dropActionTodo = {
            targetId: targetId.replace('node-', ''),
        };

        const node = container.querySelector(".tree-node");
        const targetRect = node?.getBoundingClientRect() ?? container.getBoundingClientRect();
        const oneThird = targetRect.height / 3;

        if (event.pointerPosition.y - targetRect.top < oneThird)
        {
            this.dropActionTodo["action"] = "before";
        }
        else if (event.pointerPosition.y - targetRect.top > 2 * oneThird)
        {
            this.dropActionTodo["action"] = "after";
        }
        else
        {
            this.dropActionTodo["action"] = "inside";
        }
        this.showDragInfo();
    }

    drop(event: CdkDragDrop<ITreeNode[]>): void
    {
        if (!this.dropActionTodo) return;

        const draggedItemId = event.item.data;
        const parentItemId = event.previousContainer.id;
        const targetListId = this.getParentNodeId(
            this.dropActionTodo.targetId,
            this.nodes,
            "main",
        );

        const draggedItem = this.nodeLookup[draggedItemId];

        const oldItemContainer =
            parentItemId !== "main"
                ? this.nodeLookup[parentItemId].Children
                : this.nodes;
        const newContainer =
            targetListId !== "main"
                ? this.nodeLookup[targetListId ?? ""].Children
                : this.nodes;

        let i = oldItemContainer.findIndex((c) => c.Id === draggedItemId);
        oldItemContainer.splice(i, 1);

        if (this.dropActionTodo)
        {
            switch (this.dropActionTodo.action)
            {
                case "before":
                case "after":
                    // eslint-disable-next-line no-case-declarations
                    const targetIndex = newContainer.findIndex(
                        (c) => c.Id === this.dropActionTodo?.targetId,
                    );
                    if (this.dropActionTodo.action === "before")
                    {
                        newContainer.splice(targetIndex, 0, draggedItem);
                    } else
                    {
                        newContainer.splice(targetIndex + 1, 0, draggedItem);
                    }
                    break;

                case "inside":
                    this.nodeLookup[this.dropActionTodo.targetId].Children.push(
                        draggedItem,
                    );
                    this.nodeLookup[this.dropActionTodo.targetId].Expanded =
                        true;
                    break;
            }
        }

        this.clearDragInfo(true);
    }

    getParentNodeId(
        id: string,
        nodesToSearch: ITreeNode[],
        parentId: string,
    ): string | null
    {
        for (const node of nodesToSearch)
        {
            if (node.Id === id) return parentId;

            const ret = this.getParentNodeId(id, node.Children, node.Id);
            if (ret) return ret;
        }
        return null;
    }

    getAncestorNodes(childNodeId: string): ITreeNode[] | null
    {
        const ancestors = this.findParentNodeRecursive(childNodeId, this.nodes, []);
        return ancestors.length > 0 ? ancestors : null;
    }

    private findParentNodeRecursive(
        childNodeId: string,
        nodesToSearch: ITreeNode[],
        ancestors: ITreeNode[]
    ): ITreeNode[]
    {
        for (const node of nodesToSearch)
        {
            if (node.Id === childNodeId)
            {
                return ancestors;
            }

            if (node.Children && node.Children.length > 0)
            {
                const result = this.findParentNodeRecursive(childNodeId, node.Children, [...ancestors, node]);
                if (result.length > 0)
                {
                    return result;
                }
            }
        }
        return [];
    }

    showDragInfo(): void
    {
        this.clearDragInfo();

        if (this.dropActionTodo)
        {
            document
                .getElementById("node-" + this.dropActionTodo.targetId)
                ?.classList.add("drop-" + this.dropActionTodo.action);
        }
    }

    clearDragInfo(dropped = false): void
    {
        if (dropped)
        {
            this.dropActionTodo = null;
        }
        document
            .querySelectorAll(".drop-before")
            .forEach((element) => element.classList.remove("drop-before"));
        document
            .querySelectorAll(".drop-after")
            .forEach((element) => element.classList.remove("drop-after"));
        document
            .querySelectorAll(".drop-inside")
            .forEach((element) => element.classList.remove("drop-inside"));
    }

    onKeyDown(event: KeyboardEvent, node: ITreeNode): void
    {
        if (this.independentSelection)
        {
            const isLeaf = !node.HasChildren && !(node.Children?.length > 0);
            if (this.isOwnItemEvent(event) && (event.key === ' ' || (event.key === 'Enter' && isLeaf)))
            {
                event.preventDefault();
                event.stopPropagation();
                this.toggleIndependentSelection(node);
            }
            return;
        }

        if (this.disableDragAndDrop || node.Disabled)
        {
            return;
        }

        if (event.key === ' ' && !this.keyboardMoveMode)
        {
            event.preventDefault();
            this.enterKeyboardMoveMode(node);
            event.stopPropagation();
        } else if (event.key === ' ' && this.keyboardMoveMode)
        {
            event.preventDefault();
            this.keyboardDropNode();
            event.stopPropagation();
        } else if (this.keyboardMoveMode && (event.key === 'ArrowDown' || event.key === 'ArrowUp'))
        {
            event.preventDefault();
            this.keyboardNavigateDropTarget(event.key === 'ArrowDown' ? 1 : -1);
            event.stopImmediatePropagation();
        } else if (this.keyboardMoveMode && event.key === 'Escape')
        {
            event.preventDefault();
            this.exitKeyboardMoveMode();
        }

        return;
    }

    private enterKeyboardMoveMode(node: ITreeNode): void
    {
        this.keyboardMoveMode = true;
        this.keyboardMovingNode = node;
        this.keyboardTargetNode = node;
        this.keyboardDropPosition = 'inside';

        this.buildFlattenedNodeList();

        this.addKeyboardMoveIndicator(node);

        this.updateKeyboardDropIndicator();

        this.announceToScreenReader(`Move mode activated for ${node.Display}. Use arrow keys to navigate drop location, Space to drop, Escape to cancel.`);
    }

    private exitKeyboardMoveMode(announceCancel: boolean = true): void
    {
        this.clearKeyboardMoveIndicator();
        this.clearKeyboardDropIndicators();

        const wasMoving = this.keyboardMovingNode?.Display;

        this.keyboardMoveMode = false;
        this.keyboardMovingNode = null;
        this.keyboardTargetNode = null;
        this.flattenedNodes = [];

        if (announceCancel && wasMoving)
        {
            this.announceToScreenReader(`Move mode cancelled for ${wasMoving}.`);
        }
    }

    private keyboardNavigateDropTarget(direction: number): void
    {
        this.buildFlattenedNodeList();

        if (!this.keyboardTargetNode || this.flattenedNodes.length === 0 || !this.keyboardMovingNode)
        {
            return;
        }

        const currentIndex = this.flattenedNodes.findIndex(n => n.Id === this.keyboardTargetNode!.Id);
        if (currentIndex === -1)
        {
            return;
        }

        const isCurrentDescendant = this.isDescendantOf(this.keyboardTargetNode, this.keyboardMovingNode);

        if (direction > 0)
        {
            if (isCurrentDescendant)
            {
                const nextIndex = currentIndex + 1;
                if (nextIndex < this.flattenedNodes.length)
                {
                    this.keyboardTargetNode = this.flattenedNodes[nextIndex];
                    this.keyboardDropPosition = 'before';
                }
            } else if (this.keyboardDropPosition === 'before')
            {
                this.keyboardDropPosition = 'inside';
            } else if (this.keyboardDropPosition === 'inside')
            {
                this.keyboardDropPosition = 'after';
            } else
            {
                const nextIndex = currentIndex + 1;
                if (nextIndex < this.flattenedNodes.length)
                {
                    this.keyboardTargetNode = this.flattenedNodes[nextIndex];
                    if (this.isDescendantOf(this.flattenedNodes[nextIndex], this.keyboardMovingNode))
                    {
                        this.keyboardDropPosition = 'before';
                    } else
                    {
                        this.keyboardDropPosition = 'before';
                    }
                }
            }
        } else
        {
            if (isCurrentDescendant)
            {
                const prevIndex = currentIndex - 1;
                if (prevIndex >= 0)
                {
                    this.keyboardTargetNode = this.flattenedNodes[prevIndex];
                    this.keyboardDropPosition = 'after';
                }
            } else if (this.keyboardDropPosition === 'after')
            {
                this.keyboardDropPosition = 'inside';
            } else if (this.keyboardDropPosition === 'inside')
            {
                this.keyboardDropPosition = 'before';
            } else
            {
                const prevIndex = currentIndex - 1;
                if (prevIndex >= 0)
                {
                    this.keyboardTargetNode = this.flattenedNodes[prevIndex];
                    if (this.isDescendantOf(this.flattenedNodes[prevIndex], this.keyboardMovingNode))
                    {
                        this.keyboardDropPosition = 'after';
                    } else
                    {
                        this.keyboardDropPosition = 'after';
                    }
                }
            }
        }

        this.updateKeyboardDropIndicator();
        this.scrollTargetIntoView();

        if (this.keyboardTargetNode)
        {
            const positionText = this.keyboardDropPosition === 'before' ? 'before' :
                this.keyboardDropPosition === 'after' ? 'after' : 'inside';
            this.announceToScreenReader(`Drop ${positionText} ${this.keyboardTargetNode.Display}.`);
        }
    }

    private keyboardDropNode(): void
    {
        if (!this.keyboardMovingNode || !this.keyboardTargetNode)
        {
            return;
        }

        if (this.keyboardMovingNode.Id === this.keyboardTargetNode.Id)
        {
            this.announceToScreenReader('Cannot drop a node onto itself. Move cancelled.');
            this.exitKeyboardMoveMode(false);
            return;
        }

        if (this.isDescendantOf(this.keyboardTargetNode, this.keyboardMovingNode))
        {
            this.announceToScreenReader('Cannot drop a node relative to its own descendants. Move cancelled.');
            this.exitKeyboardMoveMode(false);
            return;
        }

        const movingNodeLevel = this.getNodeLevel(this.keyboardMovingNode);
        let targetLevel: number;
        if (this.keyboardDropPosition === 'inside')
        {
            targetLevel = this.getNodeLevel(this.keyboardTargetNode) + 1;
        } else
        {
            targetLevel = this.getNodeLevel(this.keyboardTargetNode);
        }

        if (movingNodeLevel !== targetLevel)
        {
            this.announceToScreenReader('Cannot drop node at a different level. Move cancelled.');
            this.exitKeyboardMoveMode(false);
            return;
        }

        const droppedNodeId = this.keyboardMovingNode.Id;
        const movedNodeName = this.keyboardMovingNode.Display;
        const targetNodeName = this.keyboardTargetNode.Display;
        const position = this.keyboardDropPosition;

        this.removeNodeFromTree(this.keyboardMovingNode, this.nodes);

        if (this.keyboardDropPosition === 'inside')
        {
            if (!this.keyboardTargetNode.Children)
            {
                this.keyboardTargetNode.Children = [];
            }
            this.keyboardTargetNode.Children.push(this.keyboardMovingNode);
            this.keyboardTargetNode.Expanded = true;
        } else
        {
            const parentArray = this.findParentArray(this.keyboardTargetNode, this.nodes);
            if (parentArray)
            {
                const targetIndex = parentArray.findIndex(n => n.Id === this.keyboardTargetNode!.Id);
                if (targetIndex > -1)
                {
                    const insertIndex = this.keyboardDropPosition === 'before' ? targetIndex : targetIndex + 1;
                    parentArray.splice(insertIndex, 0, this.keyboardMovingNode);
                }
            }
        }

        this.exitKeyboardMoveMode(false);

        let announcement = '';
        if (position === 'inside')
        {
            announcement = `Moved ${movedNodeName} inside ${targetNodeName}.`;
        } else if (position === 'before')
        {
            announcement = `Moved ${movedNodeName} before ${targetNodeName}.`;
        } else
        {
            announcement = `Moved ${movedNodeName} after ${targetNodeName}.`;
        }
        this.announceToScreenReader(announcement);

        setTimeout(() => this.focusNode(droppedNodeId), 0);
    }

    private buildFlattenedNodeList(): void
    {
        this.flattenedNodes = [];
        this.flattenNodesRecursive(this.nodes);
    }

    private flattenNodesRecursive(nodes: ITreeNode[]): void
    {
        if (!nodes) return;

        nodes.forEach(node =>
        {
            this.flattenedNodes.push(node);
            if (node.Children && node.Children.length > 0 && node.Expanded)
            {
                this.flattenNodesRecursive(node.Children);
            }
        });
    }

    private addKeyboardMoveIndicator(node: ITreeNode): void
    {
        const element = document.querySelector(`forge-tree-item[data-node-id="${node.Id}"]`);
        if (element)
        {
            element.classList.add('keyboard-moving');
        }
    }

    private clearKeyboardMoveIndicator(): void
    {
        document.querySelectorAll('.keyboard-moving').forEach(el =>
        {
            el.classList.remove('keyboard-moving');
        });
    }

    private updateKeyboardDropIndicator(): void
    {
        this.clearKeyboardDropIndicators();

        if (!this.keyboardTargetNode)
        {
            return;
        }

        const element = document.querySelector(`forge-tree-item[data-node-id="${this.keyboardTargetNode.Id}"]`);
        if (element)
        {
            element.classList.add('keyboard-drop-' + this.keyboardDropPosition);
        }
    }

    private clearKeyboardDropIndicators(): void
    {
        document.querySelectorAll('.keyboard-drop-before, .keyboard-drop-inside, .keyboard-drop-after').forEach(el =>
        {
            el.classList.remove('keyboard-drop-before', 'keyboard-drop-inside', 'keyboard-drop-after');
        });
    }

    private scrollTargetIntoView(): void
    {
        if (!this.keyboardTargetNode)
        {
            return;
        }

        const element = document.querySelector(`forge-tree-item[data-node-id="${this.keyboardTargetNode.Id}"]`);
        if (element)
        {
            element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }

    private expandTargetNode(): void
    {
        if (!this.keyboardTargetNode)
        {
            return;
        }

        if (this.keyboardTargetNode.Children && this.keyboardTargetNode.Children.length > 0)
        {
            if (!this.keyboardTargetNode.Expanded)
            {
                this.keyboardTargetNode.Expanded = true;
                this.buildFlattenedNodeList();
                this.announceToScreenReader(`Expanded ${this.keyboardTargetNode.Display}.`);
            } else
            {
                this.announceToScreenReader(`${this.keyboardTargetNode.Display} is already expanded.`);
            }
        } else
        {
            this.announceToScreenReader(`${this.keyboardTargetNode.Display} has no children to expand.`);
        }
    }

    private collapseTargetNode(): void
    {
        if (!this.keyboardTargetNode)
        {
            return;
        }

        if (this.keyboardTargetNode.Children && this.keyboardTargetNode.Children.length > 0)
        {
            if (this.keyboardTargetNode.Expanded)
            {
                this.keyboardTargetNode.Expanded = false;
                this.buildFlattenedNodeList();
                this.announceToScreenReader(`Collapsed ${this.keyboardTargetNode.Display}.`);
            } else
            {
                this.announceToScreenReader(`${this.keyboardTargetNode.Display} is already collapsed.`);
            }
        } else
        {
            this.announceToScreenReader(`${this.keyboardTargetNode.Display} has no children to collapse.`);
        }
    }

    private focusNode(nodeId: string): void
    {
        const element = document.querySelector(`forge-tree-item[data-node-id="${nodeId}"]`) as HTMLElement;
        if (element)
        {
            element.focus();
        }
    }

    private announceToScreenReader(message: string): void
    {
        this.screenReaderAnnouncement = message;
        setTimeout(() =>
        {
            this.screenReaderAnnouncement = '';
        }, 1000);
    }

    private isDescendantOf(potentialDescendant: ITreeNode, potentialAncestor: ITreeNode): boolean
    {
        if (!potentialAncestor.Children || potentialAncestor.Children.length === 0)
        {
            return false;
        }

        for (const child of potentialAncestor.Children)
        {
            if (child.Id === potentialDescendant.Id)
            {
                return true;
            }
            if (this.isDescendantOf(potentialDescendant, child))
            {
                return true;
            }
        }

        return false;
    }

    get isKeyboardMoveMode(): boolean
    {
        return this.keyboardMoveMode;
    }
}
